/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaItpItemCard.tsx
 * FILE_ID: JOBHUB-QA-ITP-ITEM-CARD-001
 * PURPOSE: One ITP check: photos, timestamped notes, status buttons, attach.
 * WHY: The drawer and the ITP tab must render the same card. Two copies is
 *      how they drift.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { QaItemStatusButtons, type QaNote } from '@quotech/ui'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { QaPhotoSourceMenu } from '../../qa/QaPhotoSourceMenu'
import { assetForDeviceFile } from '../../qa/qaJobLibrary'
import { QaItemEvidenceStrip, splitQaEvidence } from '../../qa/QaItemEvidenceStrip'
import type { createQaApi, QaEvidence, QaItemInstance, QaItemStatus } from '../../qa/qaApi'
import { QA_STATUS_OPTIONS } from '../../qa/qaStatusOptions'
import { QaNotesDisclosure } from './QaNotesDisclosure'
import { qaErrorMessage } from './qaTabSelectors'
import { forgetEvidencePhoto, rememberEvidencePhoto } from './useQaEvidenceUrls'

export const ITP_STATUS_BUTTONS = QA_STATUS_OPTIONS

export function QaItpItemCard({
  item,
  evidence,
  jobId,
  areaId,
  api,
  onMutated,
  onSetStatus,
  statusPending = false,
  selected = false,
  onToggleSelected,
  onWriteError,
}: {
  item: QaItemInstance
  evidence: QaEvidence[]
  jobId: number
  areaId: string
  api: ReturnType<typeof createQaApi>
  onMutated: () => void
  onSetStatus: (itemId: string, status: QaItemStatus) => void
  statusPending?: boolean
  selected?: boolean
  onToggleSelected?: () => void
  onWriteError?: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [writeError, setWriteError] = useState<string | null>(null)
  const { photos, documents } = splitQaEvidence(evidence)
  const notes: QaNote[] = evidence
    .filter((row) => row.type === 'note')
    .map((row) => ({
      id: row.id,
      text: row.note_text ?? '',
      authorName: row.captured_by_name,
      at: row.captured_at ?? '',
    }))

  const runAttach = async (attach: () => Promise<void>) => {
    setUploading(true)
    try {
      await attach()
      onMutated()
    } catch (error) {
      onWriteError?.(qaErrorMessage(error, 'Could not attach that file. Please try again.'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <article
      className={cn(
        'rounded-lg border bg-card p-3 space-y-3',
        // Selection = border, not a background wash: content stays readable.
        selected ? 'border-primary ring-1 ring-primary/30' : 'border-border',
      )}
      data-selected={selected || undefined}
    >
      <header className="flex items-start justify-between gap-2">
        <h3 className="flex min-w-0 items-start gap-2 text-sm font-semibold">
          {onToggleSelected && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelected}
              aria-label={`Select item ${item.item_number}`}
              data-testid={`qa-item-select-${item.id}`}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-border"
            />
          )}
          <span className="min-w-0 truncate">
            <span className="mr-1.5 text-muted-foreground">{item.display_number ?? item.item_number}</span>
            {item.title}
          </span>
        </h3>
        {item.has_hold_point && (
          <span className="shrink-0 rounded bg-status-warn-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-status-warn">
            Hold point
          </span>
        )}
      </header>
      {item.acceptance_criteria && (
        <p className="text-xs text-muted-foreground">{item.acceptance_criteria}</p>
      )}
      {item.product && (
        <p className="truncate text-xs font-medium text-primary">{item.product}</p>
      )}

      <QaItemEvidenceStrip
        photos={photos}
        documents={documents}
        onDelete={async (id) => {
          await api.deleteEvidence(id)
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaEvidenceUrls.ts (rememberEvidencePhoto)
          forgetEvidencePhoto(queryClient, areaId, id)
          onMutated()
        }}
      />

      <QaNotesDisclosure
        itemTitle={item.title}
        notes={notes}
        onAdd={async (text) => {
          try {
            setWriteError(null)
            await api.addNoteEvidence({ areaId, itemInstanceId: item.id, noteText: text })
            onMutated()
          } catch (error) {
            const message = qaErrorMessage(error, 'Could not save that note.')
            setWriteError(message)
            onWriteError?.(message)
          }
        }}
        onDelete={async (id) => {
          try {
            setWriteError(null)
            await api.deleteEvidence(id)
            onMutated()
          } catch (error) {
            const message = qaErrorMessage(error, 'Could not delete that note.')
            setWriteError(message)
            onWriteError?.(message)
          }
        }}
        emptyLabel="No notes on this check yet."
        draftKey={`${areaId}:${item.id}`}
      />
      {writeError && <p role="alert" className="text-xs text-destructive">{writeError}</p>}

      <QaItemStatusButtons
        currentStatus={item.status}
        disabled={statusPending}
        onSelect={(status) => onSetStatus(item.id, status)}
      />

      <div className="flex flex-wrap gap-2">
        <QaPhotoSourceMenu
          busy={uploading}
          listQDriveFiles={api.listQDriveFiles}
          listLibraryFiles={() => api.listQaLibrary(jobId)}
          onPickFile={(file) => runAttach(async () => {
            // Reused from: packages/job-hub/src/jobs/qa/qaInspectionApi.ts (uploadFile) when no library match
            const id = await assetForDeviceFile(file, () => api.listQaLibrary(jobId), (next) => api.uploadFile(next, jobId))
            const created = await api.addPhotoEvidence({
              areaId, itemInstanceId: item.id, assetId: id, description: item.title,
            })
            // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaEvidenceUrls.ts (rememberEvidencePhoto)
            rememberEvidencePhoto(queryClient, areaId, created)
          })}
          onPickAsset={async (assetId) => {
            await runAttach(async () => {
              const created = await api.addPhotoEvidence({
                areaId, itemInstanceId: item.id, assetId, description: item.title,
              })
              // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaEvidenceUrls.ts (rememberEvidencePhoto)
              rememberEvidencePhoto(queryClient, areaId, created)
            })
          }}
        />
        <QaPhotoSourceMenu
          mode="file"
          busy={uploading}
          listQDriveFiles={api.listQDriveFiles}
          listLibraryFiles={() => api.listQaLibrary(jobId)}
          onPickFile={(file) => runAttach(async () => {
            // Reused from: packages/job-hub/src/jobs/qa/qaInspectionApi.ts (uploadFile) when no library match
            const id = await assetForDeviceFile(file, () => api.listQaLibrary(jobId), (next) => api.uploadFile(next, jobId))
            await api.addDocumentEvidence({
              areaId, itemInstanceId: item.id, assetId: id, description: file.name,
            })
          })}
          onPickAsset={async (assetId, filename) => {
            await runAttach(async () => {
              await api.addDocumentEvidence({
                areaId, itemInstanceId: item.id, assetId, description: filename || item.title,
              })
            })
          }}
        />
      </div>
    </article>
  )
}
